import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  ExternalLink, ChevronRight, ChevronDown, Shield, Users, Zap,
  TrendingUp, Lock, Calendar, Mail, Linkedin, ArrowRight,
  Play, BarChart3, Globe, Award, Clock, FileText, Layers,
  CheckCircle2, ArrowUpRight, Cpu, BookOpen, Eye
} from 'lucide-react';
import Header from '../components/layout/Header';
import { Footer, DebateSandbox } from '../components/marketing';

// ─── Animation helpers ───────────────────────────────────────
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const stagger = (i, base = 0.06) => ({ delay: i * base, ease: [0.16, 1, 0.3, 1] });

// ─── Provenance Badge ────────────────────────────────────────
function ProvenanceBadge({ section, transcript }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[rgba(245,241,232,0.06)] text-[rgba(245,241,232,0.32)] hover:text-[rgba(245,241,232,0.48)] hover:border-[rgba(245,241,232,0.12)] transition-all text-[9px] font-mono tracking-[0.15em] uppercase"
      >
        <Cpu className="w-2.5 h-2.5" />
        Debated by 5-model Fray committee
        <ChevronDown className={`w-2.5 h-2.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 p-3 bg-[#0A0A0A] border border-[rgba(245,241,232,0.04)] text-[10px] text-[rgba(245,241,232,0.48)] font-mono space-y-1">
              <p>Section: {section}</p>
              <p>Committee: 5 models (kimi-k2.5, minimax-m2.7, glm-5, flash-lite, grok-4.20)</p>
              <p>Consensus: 78-88% across 3-5 rounds</p>
              {transcript && (
                <a href={transcript} className="text-[#00D4FF] hover:underline flex items-center gap-1 mt-1">
                  View debate transcript <ArrowUpRight className="w-2.5 h-2.5" />
                </a>
              )}
              <p className="text-[rgba(245,241,232,0.24)] mt-2 italic">For informational purposes only. Fray does not provide investment advice.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Sticky CTA Header ──────────────────────────────────────
function StickyCTA({ visible }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -48, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed top-14 left-0 right-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[rgba(245,241,232,0.06)]"
        >
          <div className="max-w-6xl mx-auto px-4 h-10 flex items-center justify-between">
            <span className="text-[10px] font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase hidden sm:block">
              Fray · $200K Post-Money SAFE
            </span>
            <a
              href="https://calendly.com/fray-ai/investor"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#FF4D00] text-[#0A0A0A] text-[10px] font-display font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
            >
              <Calendar className="w-3 h-3" />
              Schedule a Call with Jim
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Art of the Possible Pipeline ────────────────────────────
const PIPELINE_STEPS = [
  {
    num: '01',
    title: 'Brand Identity',
    subtitle: '5 models on mushrooms',
    detail: 'Visual identity designed by a psychedelic creative committee. Palette, typography, motion language — all debated.',
    color: '#FF4D00',
    icon: Eye,
  },
  {
    num: '02',
    title: 'Content Audit',
    subtitle: 'F → A grade',
    detail: 'Committee audited every page, gave the existing site an F for 7 of 8 customer segments, then designed the fix.',
    color: '#00D4FF',
    icon: FileText,
  },
  {
    num: '03',
    title: 'Page Architecture',
    subtitle: '17 pages spec\'d',
    detail: 'Every page brief — headlines, sections, CTAs, conversion targets, SEO keywords — designed by consensus.',
    color: '#F2A900',
    icon: Layers,
  },
  {
    num: '04',
    title: 'Enterprise MCP',
    subtitle: '46-hour sprint',
    detail: 'A separate committee designed the enterprise MCP integration, async jobs, and security architecture.',
    color: '#22C55E',
    icon: Shield,
  },
  {
    num: '05',
    title: 'This Pitch',
    subtitle: 'You\'re reading it',
    detail: 'This page — the narrative, the sections, the CTAs, the meta-reveal — all designed by a 5-model Fray committee.',
    color: '#FF4D00',
    icon: BookOpen,
  },
];

// ─── Burn Rate Toggle ────────────────────────────────────────
function BurnRateToggle() {
  const [showFray, setShowFray] = useState(false);

  return (
    <div className="relative">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setShowFray(false)}
          className={`px-4 py-2 text-xs font-mono tracking-wider uppercase border transition-all ${
            !showFray ? 'border-[#A61E17] text-[#A61E17] bg-[#A61E17]/5' : 'border-[rgba(245,241,232,0.08)] text-[rgba(245,241,232,0.48)]'
          }`}
        >
          Traditional Startup
        </button>
        <button
          onClick={() => setShowFray(true)}
          className={`px-4 py-2 text-xs font-mono tracking-wider uppercase border transition-all ${
            showFray ? 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/5' : 'border-[rgba(245,241,232,0.08)] text-[rgba(245,241,232,0.48)]'
          }`}
        >
          Fray AI-Native
        </button>
      </div>

      <AnimatePresence mode="wait">
        {!showFray ? (
          <motion.div
            key="traditional"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-[#1E1E1E] border border-[#A61E17]/20 p-6"
          >
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-display-md font-display text-[#A61E17]">$150K</span>
              <span className="text-xs font-mono text-[rgba(245,241,232,0.48)]">/month burn</span>
            </div>
            <p className="text-xs text-[rgba(245,241,232,0.48)] font-mono mb-4">8-12 PEOPLE · 12-18 MONTH RUNWAY</p>
            <div className="grid grid-cols-4 gap-2">
              {['CEO', 'CTO', 'PM', 'Design Lead', 'FE Dev', 'BE Dev', 'DevOps', 'Marketing', 'Sales', 'CS', 'Ops', 'Finance'].map(role => (
                <div key={role} className="p-2 bg-[#0A0A0A] border border-[rgba(245,241,232,0.06)] text-center">
                  <Users className="w-3 h-3 text-[#A61E17] mx-auto mb-1" />
                  <span className="text-[8px] font-mono text-[rgba(245,241,232,0.48)]">{role}</span>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="fray"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-[#1E1E1E] border border-[#22C55E]/20 p-6"
          >
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-display-md font-display text-[#22C55E]">$1,700</span>
              <span className="text-xs font-mono text-[rgba(245,241,232,0.48)]">/month burn</span>
            </div>
            <p className="text-xs text-[rgba(245,241,232,0.48)] font-mono mb-4">2 PEOPLE + AI · 117-MONTH RUNWAY</p>
            <div className="flex items-center gap-4">
              <div className="p-4 bg-[#0A0A0A] border border-[#22C55E]/20 text-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#22C55E] mx-auto mb-1" />
                <span className="text-[9px] font-mono text-[#22C55E]">Jim + David</span>
              </div>
              <div className="text-xs text-[rgba(245,241,232,0.32)] font-mono">+</div>
              <div className="p-4 bg-[#0A0A0A] border border-[#FF4D00]/20 text-center flex-1">
                <Cpu className="w-5 h-5 text-[#FF4D00] mx-auto mb-1" />
                <span className="text-[9px] font-mono text-[#FF4D00]">AI Infrastructure</span>
                <p className="text-[8px] text-[rgba(245,241,232,0.32)] mt-1">10+ models · 6 frays · $20 total</p>
              </div>
            </div>
            <div className="mt-4 p-3 border-l-2 border-[#22C55E] bg-[#22C55E]/5">
              <p className="text-xs text-[rgba(245,241,232,0.72)] italic font-body">
                "We built this page for less than the cost of a single McKinsey slide."
              </p>
            </div>
            {/* Runway horizon bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-mono text-[rgba(245,241,232,0.32)]">RUNWAY</span>
                <span className="text-[9px] font-mono text-[#22C55E]">117 MONTHS →</span>
              </div>
              <div className="h-2 bg-[#0A0A0A] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full bg-gradient-to-r from-[#22C55E] to-[#22C55E]/20"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-4 text-center text-xs font-mono text-[rgba(245,241,232,0.24)]">
        88x efficiency advantage · same product output
      </p>
    </div>
  );
}

// ─── IP Moat Theater ─────────────────────────────────────────
const MOAT_ELEMENTS = [
  {
    title: 'Adversarial Deliberation + Tension Gate',
    claims: 8,
    description: 'Multi-model structured debate with consensus measurement. No other platform forces AI models to genuinely disagree and then measures the quality of their convergence.',
    color: '#FF4D00',
  },
  {
    title: 'Inter-Phase Rendering',
    claims: 7,
    description: 'AI-generated visual artifacts between debate phases that evolve with the conversation. The debate produces images, not just text.',
    color: '#00D4FF',
  },
  {
    title: 'Closed-Loop Platform + Persona Snapshots',
    claims: 7,
    description: 'Persistent AI personas that learn and evolve. Your committee gets smarter with every debate you run.',
    color: '#F2A900',
  },
];

function MoatTheater() {
  const [active, setActive] = useState(0);
  return (
    <div>
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {MOAT_ELEMENTS.map((el, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`text-left p-4 border transition-all ${
              active === i
                ? `bg-[#1E1E1E] border-opacity-40`
                : 'bg-[#1E1E1E]/30 border-[rgba(245,241,232,0.06)] hover:border-[rgba(245,241,232,0.12)]'
            }`}
            style={{ borderColor: active === i ? el.color + '66' : undefined }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 flex items-center justify-center border" style={{ borderColor: el.color + '40' }}>
                <Lock className="w-3 h-3" style={{ color: el.color }} />
              </div>
              <span className="text-[10px] font-mono tracking-wider" style={{ color: el.color }}>
                {el.claims} CLAIMS
              </span>
            </div>
            <h4 className="text-xs font-display font-bold text-[#F5F1E8] uppercase tracking-tight leading-snug">
              {el.title}
            </h4>
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="p-5 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)]"
        >
          <p className="text-sm text-[rgba(245,241,232,0.72)] font-body leading-relaxed">
            {MOAT_ELEMENTS[active].description}
          </p>
        </motion.div>
      </AnimatePresence>
      <p className="mt-4 text-xs text-[rgba(245,241,232,0.48)] font-body italic text-center">
        "We didn't patent features. We patented the future of decision-making."
      </p>
    </div>
  );
}

// ─── Expandable Section ──────────────────────────────────────
function Expandable({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[rgba(245,241,232,0.08)]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left bg-[#1E1E1E]">
        <span className="text-sm font-mono text-[#F5F1E8] tracking-wider uppercase">{title}</span>
        <ChevronDown className={`w-4 h-4 text-[rgba(245,241,232,0.48)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 bg-[#1E1E1E]/50 border-t border-[rgba(245,241,232,0.04)]">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function InvestorsPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCTA(!entry.isIntersecting),
      { threshold: 0 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <StickyCTA visible={showStickyCTA} />

      {/* ─── SECTION 1: Meta-Hero ────────────────────────── */}
      <section ref={heroRef} className="pt-24 pb-20 px-4 md:pt-36 md:pb-28 md:px-6 relative overflow-hidden">
        {/* Faint thermal glow */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.03] blur-3xl bg-[radial-gradient(circle,#FF4D00,transparent_70%)]" />

        <div className="max-w-5xl mx-auto relative">
          <motion.div {...fadeUp}>
            {/* SOC2 badge in corner */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2 px-3 py-1 border border-[rgba(245,241,232,0.08)]">
                <div className="w-2 h-2 bg-[#FF4D00] animate-thermal-pulse" />
                <span className="text-[10px] font-mono text-[rgba(245,241,232,0.48)] tracking-wider">CONFIDENTIAL INVESTOR MATERIALS</span>
              </div>
              <Link to="/security" className="flex items-center gap-1.5 px-2.5 py-1 border border-[rgba(245,241,232,0.06)] text-[10px] font-mono text-[rgba(245,241,232,0.32)] hover:text-[#00D4FF] transition-colors">
                <Shield className="w-3 h-3" />
                SOC2 IN PROGRESS
              </Link>
            </div>

            {/* Meta-reveal headline */}
            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-display-lg md:text-[5rem] md:leading-[0.92] font-display text-[#F5F1E8] uppercase mb-6"
            >
              This Investment Pitch<br />
              Was Designed by a<br />
              Committee of{' '}
              <span className="text-[#FF4D00]">5 AI Models</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-lg text-[rgba(245,241,232,0.64)] font-body max-w-2xl mb-8 leading-relaxed"
            >
              You're not reading a deck. You're witnessing Fray. Every section on this page
              was debated by a multi-model AI committee. The product built the pitch.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <a
                href="https://calendly.com/fray-ai/investor"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-7 py-3.5 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Schedule a Call with Jim
              </a>
              <a
                href="#live-demo"
                className="flex items-center justify-center gap-2 px-7 py-3.5 border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] font-display text-sm font-bold uppercase tracking-wider hover:border-[#FF4D00] transition-colors"
              >
                <Play className="w-4 h-4" />
                See It in Action
              </a>
            </motion.div>

            <ProvenanceBadge section="Hero & Narrative Structure" />
          </motion.div>
        </div>
      </section>

      {/* ─── SECTION 2: Interactive Proof (DebateSandbox) ─ */}
      <section id="live-demo" className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="02" title="Interactive Proof" />
            <p className="text-sm text-[rgba(245,241,232,0.72)] mt-3 mb-8 max-w-2xl font-body">
              Don't take our word for it. Run a Fray right now. This preset evaluates Fray itself
              as an investment — team of 2, $1,700/month burn, 22-claim patent portfolio.
            </p>
            <DebateSandbox
              preloadedPrompt="Evaluate Fray as an investment: 2-person AI-native team in Bangkok, $1,700/month burn rate, $200K raise at post-money SAFE, 22-claim patent portfolio covering adversarial deliberation, inter-phase rendering, and closed-loop platform. What are the risks, blind spots, and potential?"
              governance={false}
            />
            <ProvenanceBadge section="Investment Committee Playbook Design" />
          </motion.div>
        </div>
      </section>

      {/* ─── SECTION 3: Problem / Solution ────────────────── */}
      <section className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="03" title="Problem / Solution" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <motion.div {...fadeUp} className="p-6 bg-[#1E1E1E] border border-[#A61E17]/20">
              <span className="text-[10px] font-mono text-[#A61E17] tracking-wider uppercase">The Problem</span>
              <h3 className="text-display-sm font-display text-[#F5F1E8] uppercase mt-2 mb-3">$260B Wasted on Flawed Decisions</h3>
              <p className="text-sm text-[rgba(245,241,232,0.72)] font-body leading-relaxed">
                Every AI chatbot today gives you one perspective. It agrees with your framing,
                validates your assumptions, and confirms your bias. Single-voice AI is the most
                expensive yes-man in history.
              </p>
            </motion.div>
            <motion.div {...fadeUp} className="p-6 bg-[#1E1E1E] border border-[#22C55E]/20">
              <span className="text-[10px] font-mono text-[#22C55E] tracking-wider uppercase">The Solution</span>
              <h3 className="text-display-sm font-display text-[#F5F1E8] uppercase mt-2 mb-3">Multi-Model Adversarial Deliberation</h3>
              <p className="text-sm text-[rgba(245,241,232,0.72)] font-body leading-relaxed">
                Fray assembles committees of 5+ AI models with conflicting mandates.
                They debate your decision through structured rounds, surface blind spots,
                measure genuine consensus, and produce auditable decision memos.
              </p>
            </motion.div>
          </div>
          {/* Validation quote */}
          <motion.div {...fadeUp} className="mt-6 p-5 border-l-2 border-[#F2A900] bg-[#F2A900]/5">
            <p className="text-sm text-[rgba(245,241,232,0.85)] italic font-body">
              "Fray found 3 blind spots in my investment thesis in 11 minutes.
              My actual investment committee missed all three."
            </p>
            <p className="text-[10px] font-mono text-[#F2A900] mt-2 tracking-wider">— PE PROFESSIONAL, EX-TPG</p>
          </motion.div>
          <ProvenanceBadge section="Problem/Solution Framework" />
        </div>
      </section>

      {/* ─── SECTION 4: Economics & Moat ──────────────────── */}
      <section className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="04" title="Economics & Moat" />
          </motion.div>

          {/* Burn Rate Toggle */}
          <div className="mt-8 mb-12">
            <h3 className="text-sm font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase mb-4">Burn Rate Comparison</h3>
            <BurnRateToggle />
          </div>

          {/* IP Moat Theater */}
          <div>
            <h3 className="text-sm font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase mb-4">22-Claim Patent Portfolio</h3>
            <MoatTheater />
          </div>

          <ProvenanceBadge section="Economics & IP Strategy" />
        </div>
      </section>

      {/* ─── SECTION 5: Art of the Possible ──────────────── */}
      <section className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)] bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="05" title="The Art of the Possible" />
            <p className="text-sm text-[rgba(245,241,232,0.72)] mt-3 mb-4 max-w-2xl font-body">
              How we dogfooded Fray to produce this living pitch deck.
            </p>
            <p className="text-xs text-[rgba(245,241,232,0.48)] mb-10 font-mono">
              6 SEPARATE FRAYS · 30 AI MODELS · 0 AGENCIES · 0 CONSULTANTS · TOTAL COST: &lt;$20 · TIME: 48 HOURS
            </p>
          </motion.div>

          {/* Pipeline diagram */}
          <div className="grid md:grid-cols-5 gap-3">
            {PIPELINE_STEPS.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={stagger(i, 0.1)}
                className="group relative bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] hover:border-opacity-20 p-4 transition-all"
                style={{ '--step-color': step.color }}
              >
                {/* Top accent */}
                <div className="h-[2px] w-full absolute top-0 left-0 right-0" style={{ backgroundColor: step.color }} />

                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-mono" style={{ color: step.color }}>{step.num}</span>
                  <step.icon className="w-3.5 h-3.5" style={{ color: step.color }} />
                </div>
                <h4 className="text-xs font-display font-bold text-[#F5F1E8] uppercase tracking-tight mb-1">
                  {step.title}
                </h4>
                <p className="text-[10px] font-mono mb-2" style={{ color: step.color }}>
                  {step.subtitle}
                </p>
                <p className="text-[10px] text-[rgba(245,241,232,0.48)] leading-relaxed font-body">
                  {step.detail}
                </p>

                {/* Connector arrow (hidden on last) */}
                {i < PIPELINE_STEPS.length - 1 && (
                  <div className="hidden md:block absolute -right-2.5 top-1/2 -translate-y-1/2 z-10">
                    <ChevronRight className="w-4 h-4 text-[rgba(245,241,232,0.12)]" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <ProvenanceBadge section="Art of the Possible — Dogfood Walkthrough" />
        </div>
      </section>

      {/* ─── SECTION 6: Market & Financials ───────────────── */}
      <section className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="06" title="Market & Financials" />
          </motion.div>

          <div className="mt-8 grid md:grid-cols-3 gap-4 mb-8">
            {[
              { label: 'TAM', value: '~50K', sub: 'VC-backed startups alone', color: '#FF4D00' },
              { label: 'ARPU', value: '$49–$10K', sub: 'Pro to Enterprise', color: '#00D4FF' },
              { label: 'Gross Margin', value: '>80%', sub: 'API cost pass-through', color: '#22C55E' },
            ].map((metric, i) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={stagger(i)}
                className="p-5 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] text-center"
              >
                <span className="text-[10px] font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase">{metric.label}</span>
                <p className="text-display-sm font-display mt-1" style={{ color: metric.color }}>{metric.value}</p>
                <p className="text-[10px] font-mono text-[rgba(245,241,232,0.32)] mt-1">{metric.sub}</p>
              </motion.div>
            ))}
          </div>

          {/* Financial assumptions accordion */}
          <div className="space-y-2">
            <Expandable title="Revenue Model Assumptions">
              <div className="grid md:grid-cols-2 gap-4 text-xs text-[rgba(245,241,232,0.72)] font-body">
                <div>
                  <h5 className="text-[10px] font-mono text-[#FF4D00] tracking-wider uppercase mb-2">ARPU by Tier</h5>
                  <ul className="space-y-1">
                    <li>Free: $0 (lead gen + viral)</li>
                    <li>Student: $9/month</li>
                    <li>Pro: $49/month</li>
                    <li>Team: $99/seat/month</li>
                    <li>Enterprise: $10K+/year</li>
                  </ul>
                </div>
                <div>
                  <h5 className="text-[10px] font-mono text-[#00D4FF] tracking-wider uppercase mb-2">Conversion Funnel</h5>
                  <ul className="space-y-1">
                    <li>Visitor → Signup: 8-12%</li>
                    <li>Signup → Active: 40%</li>
                    <li>Active → Paid: 15%</li>
                    <li>Paid → Team/Enterprise: 5%</li>
                  </ul>
                </div>
              </div>
            </Expandable>
            <Expandable title="Team & Location">
              <p className="text-xs text-[rgba(245,241,232,0.72)] font-body">
                Jim Perkins (Founder/CEO) + David (Co-founder/CTO). Based in Bangkok, Thailand.
                AI-native organization — Fray committees replace traditional advisory and consulting functions.
                Total monthly burn: $1,700 (infrastructure + AI API costs).
              </p>
            </Expandable>
          </div>

          <ProvenanceBadge section="Market Sizing & Financial Model" />
        </div>
      </section>

      {/* ─── SECTION 7: The Ask ───────────────────────────── */}
      <section className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="07" title="The Ask" />
          </motion.div>

          <motion.div {...fadeUp} className="mt-8 bg-[#1E1E1E] border border-[#FF4D00]/20 p-8 relative overflow-hidden">
            <div className="h-[2px] w-full bg-[#FF4D00] absolute top-0 left-0" />

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <span className="text-display-lg font-display text-[#FF4D00]">$200K</span>
                <p className="text-sm font-mono text-[rgba(245,241,232,0.72)] mt-1 mb-4">POST-MONEY SAFE</p>
                <ul className="space-y-2 text-sm text-[rgba(245,241,232,0.72)] font-body">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E] shrink-0 mt-0.5" />
                    Phase 1: Product-market fit + first enterprise pilots
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E] shrink-0 mt-0.5" />
                    Phase 2: $1-2M milestone-gated raise at validated metrics
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E] shrink-0 mt-0.5" />
                    117-month runway at current burn means no desperate pivots
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase mb-3">Use of Funds</h4>
                {[
                  { label: 'Infrastructure & AI APIs', pct: 40, color: '#FF4D00' },
                  { label: 'Enterprise Pilots & Sales', pct: 30, color: '#00D4FF' },
                  { label: 'Patent Prosecution', pct: 20, color: '#F2A900' },
                  { label: 'Reserve', pct: 10, color: '#22C55E' },
                ].map(item => (
                  <div key={item.label} className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-[rgba(245,241,232,0.72)]">{item.label}</span>
                      <span className="text-xs font-mono" style={{ color: item.color }}>{item.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-[#0A0A0A]">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="h-full"
                        style={{ backgroundColor: item.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* CTA after The Ask */}
          <motion.div {...fadeUp} className="mt-8 text-center">
            <a
              href="https://calendly.com/fray-ai/investor"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
            >
              <Calendar className="w-4 h-4" />
              Schedule a Call with Jim
            </a>
          </motion.div>

          <ProvenanceBadge section="The Ask & Use of Funds" />
        </div>
      </section>

      {/* ─── SECTION 8: Early Believers + Living Updates ──── */}
      <section className="py-20 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionNum num="08" title="Early Believers" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <motion.div {...fadeUp} className="p-5 border-l-2 border-[#F2A900] bg-[#1E1E1E]">
              <p className="text-sm text-[rgba(245,241,232,0.85)] italic font-body mb-3">
                "Found 3 blind spots in my investment thesis in 11 minutes. My actual investment
                committee missed all three."
              </p>
              <p className="text-[10px] font-mono text-[#F2A900] tracking-wider">PE PROFESSIONAL · EX-TPG</p>
            </motion.div>
            <motion.div {...fadeUp} className="p-5 border-l-2 border-[#00D4FF] bg-[#1E1E1E]">
              <p className="text-sm text-[rgba(245,241,232,0.85)] italic font-body mb-3">
                "We're using Fray for headcount expansion decisions across APAC.
                The audit trail alone justified the investment."
              </p>
              <p className="text-[10px] font-mono text-[#00D4FF] tracking-wider">ENTERPRISE VP · APAC OPERATIONS</p>
            </motion.div>
          </div>

          {/* Living metrics */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Debates Run', value: '2,400+' },
              { label: 'Models Available', value: '10+' },
              { label: 'Enterprise Features', value: '48' },
              { label: 'Patent Claims', value: '22' },
            ].map((m, i) => (
              <motion.div
                key={m.label}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={stagger(i)}
                className="p-4 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] text-center"
              >
                <span className="text-lg font-display font-bold text-[#FF4D00]">{m.value}</span>
                <p className="text-[9px] font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase mt-0.5">{m.label}</p>
              </motion.div>
            ))}
          </div>

          <ProvenanceBadge section="Social Proof & Living Metrics" />
        </div>
      </section>

      {/* ─── Share Section ────────────────────────────────── */}
      <section className="py-12 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)] bg-[#1E1E1E]/30">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-display font-bold text-[#F5F1E8] uppercase tracking-tight">Forward to Partners</h3>
            <p className="text-xs text-[rgba(245,241,232,0.48)] font-body mt-0.5">Share this interactive pitch with co-investors.</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`mailto:?subject=Fray%20AI%20—%20Investment%20Opportunity&body=Check%20out%20Fray's%20interactive%20investor%20pitch%3A%20${encodeURIComponent(window.location.href)}`}
              className="flex items-center gap-1.5 px-4 py-2 border border-[rgba(245,241,232,0.08)] text-xs font-mono text-[rgba(245,241,232,0.72)] hover:border-[#FF4D00] hover:text-[#FF4D00] transition-all"
            >
              <Mail className="w-3 h-3" /> Email
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-2 border border-[rgba(245,241,232,0.08)] text-xs font-mono text-[rgba(245,241,232,0.72)] hover:border-[#00D4FF] hover:text-[#00D4FF] transition-all"
            >
              <Linkedin className="w-3 h-3" /> LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Global provenance footer */}
      <div className="py-6 px-4 border-t border-[rgba(245,241,232,0.04)] text-center">
        <p className="text-[9px] font-mono text-[rgba(245,241,232,0.20)] tracking-wider">
          THIS PAGE WAS DESIGNED BY FRAY · 6 COMMITTEE SESSIONS · 30 AI MODELS · MARCH 2026
        </p>
      </div>

      <Footer />
    </div>
  );
}

function SectionNum({ num, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono text-[#FF4D00]">{num}</span>
      <div className="w-8 h-px bg-[#FF4D00]/40" />
      <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">{title}</h2>
    </div>
  );
}
